// eslint-disable-next-line import/no-unresolved
import { CustomerSucessController } from "@talentbait/shared-components";

import logger from "../../../utils/LoggerFrontend";
import moment from "moment";
import _ from "lodash";
import firstBy from "thenby";
const { scaleLinear } = require("d3-scale");

import { takeScreenShot, download } from "./CreativesPerformanceTableReport.utils";
import {
  CONVERSIONS,
  CONVERSIONS_FIELD_TEXT,
  REACH,
  REACH_FIELD_TEXT,
  TRAFFIC
} from "./CreativesPerformanceTableReport.constants";

// Helper method to send message to customer success
export const customerSuccessSender = async (message, data) => {
  CustomerSucessController.send(message, data);
  logger.log(
    "AdminRead--Creatives_Performance:customerSuccessSender",
    "Message sent to customer success",
    { message, data }
  );
};

/**
 * Creates a function that aggregates creative metrics from Facebook channel campaigns.
 *
 * @param {Array} fbChannelCampaigns - The array of Facebook channel campaigns.
 */
export const createGetAggregatedCreativesMetrics = (fbChannelCampaigns) => () => {
  /**
   * Retrieves the latest channel campaign based on created date.
   *
   * @param {Array} campaigns - The array of channel campaigns.
   * @returns {Object} The latest channel campaign.
   */
  const getLatestChannelCampaign = (campaigns) =>
    campaigns.slice().sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))[0];

  /**
   * Checks if an image is from Shutterstock based on platform ads and asset hash.
   *
   * @param {Array} platformAds - The array of platform ads.
   * @param {string} assetHash - The hash of the creative asset.
   * @returns {boolean} True if the image is from Shutterstock, false otherwise.
   */
  const isShutterstockImage = (platformAds, assetHash) =>
    platformAds.some((ad) => ad.assetId !== null && ad.platformMediaId === assetHash);

  /**
   * Generates results object for a creative metric.
   *
   * @param {Object} params - The parameters object
   * @param {string} params.campaignGoal - The campaign goal.
   * @param {number} params.conversions - The number of conversions.
   * @param {number} params.reach - The reach metric.
   * @param {Array} params.platformAds - The array of platform ads.
   * @param {string} params.assetHash - The hash of the creative asset.
   * @returns The results object.
   */
  const getResults = ({ campaignGoal, conversions = 0, reach = 0, platformAds, assetHash }) => {
    const value = campaignGoal !== TRAFFIC ? conversions : reach;
    return {
      field: campaignGoal !== TRAFFIC ? CONVERSIONS : REACH,
      fieldText: campaignGoal !== TRAFFIC ? CONVERSIONS_FIELD_TEXT : REACH_FIELD_TEXT,
      value: parseInt(value, 10),
      isShutterstockImage: isShutterstockImage(platformAds, assetHash)
    };
  };

  const latestChannelCampaign = getLatestChannelCampaign(fbChannelCampaigns);

  if (!latestChannelCampaign) {
    return [];
  }

  const { channel, platformCampaignId, adsetCampaignId, creativesMetrics, platformAds } =
    latestChannelCampaign;
  const metrics = {};

  for (const creativeMetrics of creativesMetrics) {
    const {
      asset: { id: assetId, hash: assetHash },
      campaignGoal,
      conversions,
      reach
    } = creativeMetrics;

    metrics[assetId] = {
      channel,
      platformCampaignId,
      adsetCampaignId,
      results: getResults({ campaignGoal, conversions, reach, platformAds, assetHash }),
      ...creativeMetrics
    };
  }

  return Object.values(metrics);
};

export const createDownloadScreenshot = (setShowAdminData, userProfile, refForScreenShot) => () => {
  setShowAdminData(false);
  takeScreenShot(refForScreenShot.current)
    .then(download)
    .finally(async () => {
      setShowAdminData(true);
      // Send to customer success a new message notification
      customerSuccessSender("CampaignAdmin:CreativeMetricsPerformance:Download");
      logger.log(
        "CampaignAdmin:CreativeMetricsPerformance:DownloadScreenshot",
        "Table screenshot download",
        { user: userProfile }
      );
    })
    .catch((error) => {
      logger.log(
        "CampaignAdmin:CreativeMetricsPerformance:DownloadScreenshot",
        "Error downloading screenshot",
        { error }
      );
    });
};

export const createGetDates = (fbChannelCampaigns) => () => {
  for (const channelCampaign of fbChannelCampaigns) {
    const lifetimeMetrics = channelCampaign?.metrics || {};

    const { startDate, endDate } = lifetimeMetrics;

    if (!startDate || !moment(startDate).isValid() || !endDate || !moment(endDate).isValid())
      continue;

    const activeDays = moment().diff(moment(startDate), "d");

    return {
      startDate,
      endDate,
      activeDays
    };
  }

  return {
    startDate: undefined,
    endDate: undefined,
    activeDays: undefined
  };
};

export const createGetPlatformAds = (fbChannelCampaigns) => {
  const platformAds = fbChannelCampaigns.flatMap(
    (channelCampaign) => channelCampaign.platformAds || []
  );
  return { platformAds };
};

export const createGetHighestImpressionsAndSortedMetrics = (props) => {
  const { creativesMetrics, defaultSortingField, defaultSortOrder, platformAds } = props;

  // Iterates creative metrics and renders Metrics table
  const creativesMetricsSorted = creativesMetrics
    .map((creative) => {
      const assetFound = platformAds.find((ad) => ad.platformMediaId === creative.asset.hash);
      return {
        ...creative,
        isFromShutterstock: !!(assetFound && assetFound.assetId),
        conversions: parseInt(creative.conversions || 0, 10)
      };
    })
    .sort(
      firstBy((element) => (element.impressions < 1000 ? 1 : 0))
        .thenBy((a, b) => {
          const aVal = parseFloat(_.get(a, defaultSortingField)) || 0;
          const bVal = parseFloat(_.get(b, defaultSortingField)) || 0;
          return defaultSortOrder === 1 ? aVal - bVal : bVal - aVal;
        })
        .thenBy((a, b) => {
          if (defaultSortOrder == 1) {
            return (
              parseFloat(_.get(a, defaultSortingField)) - parseFloat(_.get(b, defaultSortingField))
            );
          }
          if (defaultSortOrder == -1) {
            return (
              parseFloat(_.get(b, defaultSortingField)) - parseFloat(_.get(a, defaultSortingField))
            );
          }
          return 0;
        })
    );

  const impressionsValues = creativesMetricsSorted
    .map((metric) => metric.impressions)
    .filter((impression) => impression !== undefined);

  // Find impression with highest value
  const highestImpressionValue = Math.max(...impressionsValues);

  logger.log("AdminRead--Creatives__Performance", "highest impression index: ", {
    highestImpressionValue,
    creativesMetricsSorted
  });

  return { highestImpressionValue, creativesMetricsSorted };
};

export const calculateCTRBarsScale = (creativesMetrics) => {
  const bestCTR = creativesMetrics
    .map((metrics) => parseFloat(metrics.ctr))
    .sort()
    .reverse();

  const maxCTR = Math.max(...bestCTR);
  const scale = scaleLinear().domain([0, maxCTR]).range([0, 90]);

  return scale;
};

export const calculateCTRValue = ({ inlineClicks, impressions }) => {
  const ctr = parseFloat((inlineClicks / impressions) * 100);
  return ctr;
};

// Main function to create creativesMetrics
export const createComponentData = (channelCampaigns, defaultSortingField, defaultSortOrder) => {
  // Gets Facebook and Instagram channel campaigns
  const fbChannelCampaigns = (channelCampaigns || []).filter(
    (channelCampaign) =>
      (channelCampaign.channel === "facebook" || channelCampaign.channel === "instagram") &&
      channelCampaign.creativesMetrics
  );

  // Get start end and active days
  const { startDate, endDate, activeDays } = createGetDates(fbChannelCampaigns);

  const { platformAds = [] } = createGetPlatformAds(fbChannelCampaigns);

  // Maps creative metrics
  const creativesMetrics = createGetAggregatedCreativesMetrics(fbChannelCampaigns)();
  const metricsLastFetchDay = moment(creativesMetrics[0]?.snapshot_date).format("DD.MM.YYYY");

  // Defines highestImpressionsValue and creative metrics sorted
  const { highestImpressionValue, creativesMetricsSorted } =
    createGetHighestImpressionsAndSortedMetrics({
      creativesMetrics,
      defaultSortingField,
      defaultSortOrder,
      platformAds
    });

  const scale = calculateCTRBarsScale(creativesMetrics);
  return {
    startDate,
    endDate,
    activeDays,
    highestImpressionValue,
    scale,
    creativesMetricsSorted,
    metricsLastFetchDay
  };
};

export const calculateActiveDays = ({ date_start, campaignDuration }) => {
  const startDate = new Date(date_start);
  const date_end = new Date(date_start);
  date_end.setDate(startDate.getDate() + campaignDuration);
  const currentDate = new Date();

  const timeDifferenceInMs = currentDate - startDate;
  const timeDifferenceInDays = timeDifferenceInMs / (24 * 60 * 60 * 1000);

  // This line is comparing the campaign duration with the time difference in days
  // and returns the minimum value between them, so we never have a value greater than the campaign duration
  // This is also to avoid having a negative value when the campaign duration is greater than the time difference in days
  const creativeActiveDays = Math.max(
    0,
    Math.min(campaignDuration, Math.floor(timeDifferenceInDays))
  );
  return creativeActiveDays;
};
